import { useState } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import AdminLayout from '../../components/layout/AdminLayout'

export default function MatchesIndex({ matches, filters, stats }) {
  const [selectedStatus, setSelectedStatus] = useState(filters.status || '')
  const [selectedInternal, setSelectedInternal] = useState(filters.internal || '')

  const applyFilters = (newFilters) => {
    router.get('/matches', {
      ...filters,
      ...newFilters
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }

  const clearFilters = () => {
    setSelectedStatus('')
    setSelectedInternal('')
    router.get('/matches', {}, { preserveState: true })
  }

  const hasActiveFilters = filters.status || filters.internal

  const handleAccept = (matchId) => {
    router.post(`/matches/${matchId}/accept`, {}, {
      preserveScroll: true
    })
  }

  const handleReject = (matchId) => {
    if (confirm('Voulez-vous vraiment rejeter ce match ?')) {
      router.post(`/matches/${matchId}/reject`, {}, {
        preserveScroll: true
      })
    }
  }

  return (
    <AdminLayout>
      <Head title="Matches" />

      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Matches</h1>
          <p className="mt-2 text-sm text-gray-600">
            Rapprochements entre vos leads et les biens du réseau
          </p>
        </div>

        {/* Stats Cards */}
        <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5">
          <StatsCard title="Total" value={stats.total} color="blue" />
          <StatsCard title="En attente" value={stats.pendingConsent} color="yellow" />
          <StatsCard title="Proposés" value={stats.proposed} color="gray" />
          <StatsCard title="Acceptés" value={stats.accepted} color="green" />
          <StatsCard title="Complétés" value={stats.completed} color="indigo" />
        </div>

        {/* Filters */}
        <div className="mb-6 bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Filtres</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {/* Status Filter */}
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                Statut
              </label>
              <select
                id="status"
                value={selectedStatus}
                onChange={(e) => {
                  setSelectedStatus(e.target.value)
                  applyFilters({ status: e.target.value })
                }}
                className="w-full rounded-lg border border-gray-300 focus:border-primary-500 focus:ring-primary-500 focus:ring-2 focus:ring-offset-1 focus:outline-none"
              >
                <option value="">Tous les statuts</option>
                <option value="proposed">Proposé</option>
                <option value="accepted">Accepté</option>
                <option value="rejected">Rejeté</option>
                <option value="completed">Complété</option>
              </select>
            </div>

            {/* Internal/Network Filter */}
            <div>
              <label htmlFor="internal" className="block text-sm font-medium text-gray-700 mb-2">
                Type de match
              </label>
              <select
                id="internal"
                value={selectedInternal}
                onChange={(e) => {
                  setSelectedInternal(e.target.value)
                  applyFilters({ internal: e.target.value })
                }}
                className="w-full rounded-lg border border-gray-300 focus:border-primary-500 focus:ring-primary-500 focus:ring-2 focus:ring-offset-1 focus:outline-none"
              >
                <option value="">Tous les types</option>
                <option value="true">Interne (mon agence)</option>
                <option value="false">Réseau (autre agence)</option>
              </select>
            </div>

            {/* Clear Filters Button */}
            {hasActiveFilters && (
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={clearFilters}
                  className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                >
                  Réinitialiser
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Matches List */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {matches.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-500">
              {hasActiveFilters ? 'Aucun match trouvé avec ces filtres' : 'Aucun match pour le moment'}
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {matches.map((match) => (
                <MatchCard
                  key={match.id}
                  match={match}
                  onAccept={() => handleAccept(match.id)}
                  onReject={() => handleReject(match.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  )
}

// Match Card Component
function MatchCard({ match, onAccept, onReject }) {
  const canAccept = match.status === 'proposed' && !match.sourceAgencyConsent && !match.targetAgencyConsent
  const isPending = match.status === 'proposed'

  return (
    <div className="p-6 hover:bg-gray-50 transition-colors">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          {/* Match Header */}
          <div className="flex items-center gap-3 mb-4">
            <ScoreBadge score={match.relevanceScore} label={match.scoreLabel} color={match.scoreColor} />
            <StatusBadge status={match.status} />
            {match.internalMatch && (
              <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
                Interne
              </span>
            )}
          </div>

          {/* Lead & Property Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Lead */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-800 mb-2">Lead</h4>
              <p className="font-semibold text-gray-900">{match.lead.full_name}</p>
              <p className="text-sm text-gray-600">{translateProjectType(match.lead.project_type)}</p>
              {match.lead.budget && (
                <p className="text-sm text-gray-600">Budget: {formatPrice(match.lead.budget)}</p>
              )}
              {match.lead.location && (
                <p className="text-sm text-gray-600">{match.lead.location}</p>
              )}
            </div>

            {/* Property */}
            <div className="bg-green-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-green-800 mb-2">Bien</h4>
              <p className="font-semibold text-gray-900">{match.property.title}</p>
              <p className="text-sm text-gray-600">{match.property.city}</p>
              <p className="text-sm text-gray-600">
                {formatPrice(match.property.price)}
                {match.property.surface && ` - ${match.property.surface} m²`}
                {match.property.rooms && ` - ${match.property.rooms} pièces`}
              </p>
            </div>
          </div>

          {/* Agencies */}
          <div className="mt-4 flex items-center gap-4 text-sm text-gray-500">
            <span>
              <strong>Source:</strong> {match.sourceAgency.name}
              {match.sourceAgencyConsent && <span className="text-green-600 ml-1">✓</span>}
            </span>
            <span className="text-gray-300">|</span>
            <span>
              <strong>Cible:</strong> {match.targetAgency.name}
              {match.targetAgencyConsent && <span className="text-green-600 ml-1">✓</span>}
            </span>
          </div>

          {/* Date */}
          <p className="mt-2 text-xs text-gray-400">
            Créé le {new Date(match.createdAt).toLocaleDateString('fr-FR', {
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            })}
          </p>
        </div>

        {/* Actions */}
        <div className="ml-6 flex flex-col gap-2">
          <Link
            href={`/matches/${match.id}`}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
          >
            Voir détails
          </Link>
          {isPending && (
            <>
              <button
                onClick={onAccept}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors"
              >
                Accepter
              </button>
              <button
                onClick={onReject}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100 transition-colors"
              >
                Rejeter
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// Stats Card Component
function StatsCard({ title, value, color = 'blue' }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-700',
    green: 'bg-green-50 text-green-700',
    yellow: 'bg-yellow-50 text-yellow-700',
    indigo: 'bg-indigo-50 text-indigo-700',
    gray: 'bg-gray-50 text-gray-700',
  }

  return (
    <div className="overflow-hidden bg-white rounded-lg shadow">
      <div className="px-4 py-5 sm:p-6">
        <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
        <dd className={`mt-1 text-3xl font-semibold ${colorClasses[color]}`}>{value}</dd>
      </div>
    </div>
  )
}

// Score Badge Component
function ScoreBadge({ score, label, color }) {
  const colorClasses = {
    green: 'bg-green-100 text-green-800 border-green-200',
    yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    orange: 'bg-orange-100 text-orange-800 border-orange-200',
    red: 'bg-red-100 text-red-800 border-red-200',
  }

  return (
    <span className={`inline-flex items-center px-3 py-1 text-sm font-semibold rounded-full border ${colorClasses[color] || colorClasses.yellow}`}>
      {score}% - {label}
    </span>
  )
}

// Status Badge Component
function StatusBadge({ status }) {
  const statusConfig = {
    proposed: { label: 'Proposé', color: 'bg-gray-100 text-gray-800' },
    accepted: { label: 'Accepté', color: 'bg-green-100 text-green-800' },
    rejected: { label: 'Rejeté', color: 'bg-red-100 text-red-800' },
    completed: { label: 'Complété', color: 'bg-indigo-100 text-indigo-800' },
  }

  const config = statusConfig[status] || statusConfig.proposed

  return (
    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${config.color}`}>
      {config.label}
    </span>
  )
}

// Helper functions
function translateProjectType(type) {
  const translations = {
    'buyer': 'Acheteur',
    'seller': 'Vendeur',
    'tenant': 'Locataire',
    'landlord': 'Propriétaire',
    'achat': 'Achat',
    'vente': 'Vente',
    'location': 'Location'
  }
  return translations[type] || type
}

function formatPrice(price) {
  if (!price) return ''
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0
  }).format(price)
}
